import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable, Subject} from 'rxjs';
import {Endpoints} from '../../../../models/endpoints';
import {HttpService} from '../../../shared/services/http.service';
import {TypeAnn} from '../../common/models/type.ann';
import {ProcurementProjectLot} from '../model/forms/commonaggregatecomponents/ProcurementProjectLot';
import {IndexTrackerService} from "./index-tracker.service";
import {CanNavigateInStepperService} from "../../../shared/services/can-navigate-in-stepper.service";
import {EformsToolsService} from "./e-forms-tools.service";
import {tap, map} from "rxjs/operators";

@Injectable({
  providedIn: 'root'
})
export class EformDuplicableEntityService {

  public actuaEntityIndex: number = -1;
  public isEformDuplicableEntityReady: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public actualSectionDuplicableCount: number = 0;
  public initialSectionDuplicableIndex: number = -1;
  // ✅ FIX 2026-03-18: Nombre de pages LOT/PAR dans le stepper, géré uniquement par addLotsToStepper/addNewLot/removeLot
  // Utilisé par getSectionDuplicableIndexes() pour déterminer le nombre correct de sections duplicables
  // (ne dépend pas de listDuplicableEntity qui peut contenir des éléments supplémentaires)
  public stepperLotPageCount: number = 0;
  public typeAnnForDuplicableSection: BehaviorSubject<TypeAnn> = new BehaviorSubject(null);
  public listDuplicableEntity: BehaviorSubject<any[]> = new BehaviorSubject([]);
  public duplicableSectionChargeEnded: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public currentDuplicableSectionValue: BehaviorSubject<ProcurementProjectLot> = new BehaviorSubject(null);
  public duplicableSectionAvisLieIsCreated: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public missDuplicableSectionInit: boolean = false;
  public isCreationButBeforeFirstSave: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public currentAvisId: BehaviorSubject<number> = new BehaviorSubject(null);
  public needSaveAfterChangeAccordion: BehaviorSubject<boolean> = new BehaviorSubject(false);

  // ✅ ROBUSTE 2025-12-16: Système de sauvegarde synchrone sans debounce
  // Subject pour demander une sauvegarde immédiate (sans debounce)
  public requestImmediateSave$ = new Subject<void>();
  // Subject pour notifier quand la sauvegarde est terminée
  public saveCompleted$ = new Subject<void>();

  // 🔧 FIX 2026-02-20: Système basé sur les événements pour rouvrir un accordéon après suppression
  // Le service stocke la demande en attente et l'envoie quand le composant signale qu'il est prêt
  public requestOpenAccordion$ = new Subject<{ type: string, index: number }>();
  private pendingAccordionOpen: { type: string, index: number } | null = null;
  private readyComponents = new Set<string>(); // Format: "TYPE_INDEX" ex: "TPA_0"

  // ✅ FIX 2026-03-05: Subject pour notifier quand un composant duplicable est créé et prêt
  // Utilisé pour attendre la création d'un nouveau lot avant d'ouvrir son accordéon
  public componentReady$ = new Subject<{ type: string, index: number }>();

  private duplicableEntitiesSubjects = new Map<string, BehaviorSubject<number>>();
  private loadedDuplicableElements = new Set<string>(); // Track des éléments déjà chargés
  private displayedElementIndexes = new Map<string, number>(); // Track de l'index affiché par type
  private expandedMapSubject = new BehaviorSubject<Map<string, number>>(new Map());
  public expanded$ = this.expandedMapSubject.asObservable();
  public firstSaveIsEndedForAvisLie: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public isFirstSaveAndPreremp: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public needWaitEndUpdateForActiveControls: boolean = false;

  // Behavior Subject de type boolean pour déterminer si toute les infos de l'avis ont été chargées du back et sont prêtes à être utilisées
  public isAvisWithAllDuplicablesElementsReady: BehaviorSubject<boolean> = new BehaviorSubject(false);


  // Behavior Subject de type boolean alertant lorsque le formulaire (form) à finit d'être crée
  public isFormReady: BehaviorSubject<boolean> = new BehaviorSubject(false);

  public isDataDuplicableEntityReady: BehaviorSubject<boolean> = new BehaviorSubject(false);

  public organisationNotice: any = null;
  public resultNotice: any = null;
  public tenderNotice: any = null;
  public settledContractNotice: any = null;
  private _tenderingPartyNotice: any = null;

  // 🔧 FIX 2026-02-17: Getter qui retourne toujours une copie propre du template
  // pour éviter les mutations involontaires
  public get tenderingPartyNotice(): any {
    if (!this._tenderingPartyNotice) return null;
    // Retourner une copie profonde pour éviter les mutations
    const copy = JSON.parse(JSON.stringify(this._tenderingPartyNotice));
    //Suppression temporaire du point de contact spécifique
    if(copy?.subGroups){
      copy.subGroups = copy.subGroups.filter(g=>g.id !='GR-Touch-Point');
    }
    return copy;
  }

  public set tenderingPartyNotice(value: any) {
    this._tenderingPartyNotice = value;
  }

  public avisWithElementsDuplicable: BehaviorSubject<any> = new BehaviorSubject(null);

  public actuallyIndexLotOrPar = 0;

  constructor(private http: HttpService, private canNavigateInStepperService: CanNavigateInStepperService,
              private eformsToolsService: EformsToolsService, private indexTrackerService: IndexTrackerService) {
    this.initializeIndexTracker();
  }


  /**
   * Permet l'enregistrement d'un objet duplicable avec une clé unique pour pouvoir ensuite charger n'importe quel objet
   * duplicable à la demande
   * @param key
   */
  public registerDuplicableEntity(key: string) {
    if (!this.duplicableEntitiesSubjects.has(key)) {
      this.duplicableEntitiesSubjects.set(key, new BehaviorSubject(null));
    }
    return this.duplicableEntitiesSubjects.get(key)!; // Retourne le sujet associé à l'objet duplicable
  }

  /**
   * Remet à zero la liste des éléments duplicable du formulaire (quand on le quitte)
   */
  public resetDuplicableEntitiesSubjects() {
    this.duplicableEntitiesSubjects = new Map<string, BehaviorSubject<number>>();
    this.loadedDuplicableElements.clear();
    // Reset des index affichés pour chaque type d'élément duplicable
    this.displayedElementIndexes.clear();
    this.expandedMapSubject.next(new Map());
    // Reset des données d'éléments duplicables extraits
    this.organisationNotice = null;
    this.resultNotice = null;
    this.tenderNotice = null;
    this.settledContractNotice = null;
    this.tenderingPartyNotice = null;
    this.avisWithElementsDuplicable.next(null);
    this.actuallyIndexLotOrPar = 0;
  }

  /**
   * Marque un élément duplicable comme déjà chargé
   * @param type Le type de l'élément (ORG, CON, TEN, etc.)
   * @param index L'index de l'élément
   */
  public markDuplicableElementAsLoaded(type: string, index: number) {
    const key = `${type}_${index}`;
    this.loadedDuplicableElements.add(key);
  }

  /**
   * Vérifie si un élément duplicable a déjà été chargé
   * @param type Le type de l'élément (ORG, CON, TEN, etc.)
   * @param index L'index de l'élément
   * @returns true si l'élément a déjà été chargé
   */
  public isDuplicableElementLoaded(type: string, index: number): boolean {
    const key = `${type}_${index}`;
    return this.loadedDuplicableElements.has(key);
  }

  /**
   * Retire le marqueur de chargement d'un élément (utile en cas de suppression)
   * @param type Le type de l'élément (ORG, CON, TEN, etc.)
   * @param index L'index de l'élément
   */
  public unmarkDuplicableElementAsLoaded(type: string, index: number) {
    const key = `${type}_${index}`;
    this.loadedDuplicableElements.delete(key);
  }

  /**
   * Réindexe les éléments chargés après une suppression
   * Exemple : si on supprime CON_2 et qu'on a CON_0, CON_1, CON_2, CON_3, CON_4 chargés,
   * on doit avoir CON_0, CON_1, CON_2, CON_3 (CON_3 devient l'ancien CON_4, etc.)
   * @param type Le type de l'élément (ORG, CON, TEN, etc.)
   * @param deletedIndex L'index de l'élément supprimé
   * @param totalCount Le nombre total d'éléments APRÈS suppression
   */
  public reindexLoadedElementsAfterDeletion(type: string, deletedIndex: number, totalCount: number) {
    // Créer une liste des index actuellement chargés pour ce type
    const loadedIndexes: number[] = [];
    this.loadedDuplicableElements.forEach(key => {
      if (key.startsWith(`${type}_`)) {
        const index = parseInt(key.split('_')[1]);
        if (!isNaN(index)) {
          loadedIndexes.push(index);
        }
      }
    });

    // Trier les index
    loadedIndexes.sort((a, b) => a - b);

    // Supprimer toutes les clés de ce type
    this.loadedDuplicableElements.forEach(key => {
      if (key.startsWith(`${type}_`)) {
        this.loadedDuplicableElements.delete(key);
      }
    });

    // Recréer les clés avec les nouveaux index
    loadedIndexes.forEach(oldIndex => {
      if (oldIndex < deletedIndex) {
        // Les éléments avant celui supprimé gardent leur index
        this.loadedDuplicableElements.add(`${type}_${oldIndex}`);
      } else if (oldIndex > deletedIndex) {
        // Les éléments après celui supprimé voient leur index diminuer de 1
        this.loadedDuplicableElements.add(`${type}_${oldIndex - 1}`);
      }
      // L'élément à deletedIndex n'est pas rajouté (il est supprimé)
    });
  }

  /**
   * Trigger l'élément duplicable à partir du nom du groupe et de l'index et attend un retour
   * @param key
   * @param index
   */
  public triggerDuplicableEntity(key: string, index: number) {
    const duplicableEntitiesSubject = this.duplicableEntitiesSubjects.get(key + '_' + index);
    if (duplicableEntitiesSubject) {
      duplicableEntitiesSubject.next(+index); // Notifie l'objet duplicable
    }
  }

  /**
   * Définit l'index de l'élément affiché pour un type donné
   */
  public setDisplayedElementIndex(type: string, index: number): void {
    this.displayedElementIndexes.set(type, index);
    // Notifier les observateurs du changement d'état d'expansion
    this.expandedMapSubject.next(new Map(this.displayedElementIndexes));
  }

  /**
   * Récupère l'index de l'élément affiché pour un type donné
   */
  public getDisplayedElementIndex(type: string): number {
    // 🔧 FIX 2026-01-22: Pour les LOTs et PARs, utiliser actuallyIndexLotOrPar
    // car les lots sont des pages du stepper, pas des accordéons
    if ((type === 'LOT' || type === 'PAR') && this.actuallyIndexLotOrPar >= 0) {
      return this.actuallyIndexLotOrPar;
    }

    // Si aucun index n'est défini, on retourne 0 par défaut
    // pour que le premier élément s'ouvre automatiquement
    if (!this.displayedElementIndexes.has(type)) {
      return 0;
    }
    return this.displayedElementIndexes.get(type) ?? 0;
  }

  /**
   * Réinitialise l'index affiché pour un type donné (ferme tous les accordéons)
   */
  public closeAllAccordions(type: string): void {
    this.displayedElementIndexes.set(type, -1);
    // Notifier les observateurs du changement d'état d'expansion
    this.expandedMapSubject.next(new Map(this.displayedElementIndexes));
  }

  /**
   * 🔧 FIX 2026-02-20: Demande la réouverture d'un accordéon spécifique
   * Envoie directement la demande - l'appelant doit s'assurer que le timing est correct
   * (généralement via setTimeout(0) après detectChanges)
   */
  public requestOpenAccordion(type: string, index: number): void {
    this.requestOpenAccordion$.next({ type, index });
  }

  /**
   * 🔧 FIX 2026-02-20: Appelé par le composant DuplicableElement quand il est initialisé et prêt
   * ✅ FIX 2026-03-05: Émettre un événement pour notifier que le composant est prêt
   */
  public notifyComponentReady(type: string, index: number): void {
    const componentKey = `${type}_${index}`;
    this.readyComponents.add(componentKey);
    this.componentReady$.next({ type, index });
  }

  /**
   * 🔧 FIX 2026-02-20: Appelé quand un composant est détruit
   */
  public notifyComponentDestroyed(type: string, index: number): void {
    const componentKey = `${type}_${index}`;
    this.readyComponents.delete(componentKey);
  }

  /**
   * 🔧 FIX 2026-02-20: Réinitialise les composants prêts pour un type donné
   * Conservé pour compatibilité
   */
  public clearReadyComponentsForType(type: string): void {
    const keysToDelete: string[] = [];
    this.readyComponents.forEach(key => {
      if (key.startsWith(`${type}_`)) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach(key => this.readyComponents.delete(key));
  }

  /**
   * Lance la suppression d'une entité duplicable du backend
   *
   * @param avisId
   * @param index
   * @param type
   */
  public deleteDuplicableEntity(avisId: number, index: number, type: string): Observable<any> {
    return this.http.delete(
      Endpoints.DUPLICABLE_ENTITY
      + Endpoints.AVIS_ID + avisId
      + Endpoints.INDEX + index
      + Endpoints.TYPE + type
    );
  }

  /**
   * Lance un appel pour déterminer les ids et les noms des entités duplicables pour un avis
   *
   * @param avisId
   */
  public getIdsAndNamesByAvisId(avisId: number): Observable<any> {
    return this.http.get(
      Endpoints.DUPLICABLE_ENTITY
      + Endpoints.LIST_IDS
      + Endpoints.AVIS_ID
      + avisId
    ).pipe(
      map((list: any[]) => this.enrichListWithElementsFromAvis(list))
    );
  }

  /**
   * Enrichit la liste des IDs retournée par le backend avec les éléments qui existent
   * dans avisWithElementsDuplicable mais qui n'ont pas d'ID technique.
   * Cela permet de compter correctement les éléments CON, TEN, RES, TPA même s'ils
   * n'ont pas d'ID technique (cas des éléments facultatifs vides).
   */
  private enrichListWithElementsFromAvis(list: any[]): any[] {
    if (!Array.isArray(list)) return list;

    const avisData = this.avisWithElementsDuplicable.getValue();
    if (!avisData?.jsonsaisieformulaire?.ann) return list;

    const ann = avisData.jsonsaisieformulaire.ann;
    const enrichedList = [...list];

    // Trouver le formulaire (contractnotice, contractawardnotice, priorinformationnotice, etc.)
    const formKey = Object.keys(ann).find(key =>
      key.includes('notice') && typeof ann[key] === 'object' && ann[key] !== null
    );
    if (!formKey) return list;

    // Mapping des types vers les chemins dans l'avis
    const typeConfig: { [key: string]: string } = {
      'CON': 'settledcontract',
      'TEN': 'lottender',
      'RES': 'lotresult',
      'TPA': 'tenderingparty'
    };

    // Naviguer jusqu'à noticeresult
    const noticeResult = ann[formKey]?.ublextensions?.ublextension?.[0]?.extensioncontent?.eformsextension?.noticeresult;
    if (!noticeResult) return list;

    for (const [type, arrayName] of Object.entries(typeConfig)) {
      const elementsInAvis = noticeResult[arrayName];
      if (!Array.isArray(elementsInAvis)) continue;

      // ✅ FIX 2025-12-24: Filtrer les éléments vides ou sans données significatives
      const validElementsInAvis = elementsInAvis.filter(element => this.isValidDuplicableElement(element, type));

      const countInAvis = validElementsInAvis.length;
      const countWithValidId = list.filter(e => e && e.id && e.id.startsWith(type + '-')).length;

      if (countInAvis > countWithValidId) {
        for (let i = countWithValidId; i < countInAvis; i++) {
          const tempId = `${type}-NOID${String(i + 1).padStart(4, '0')}`;
          enrichedList.push({
            id: tempId,
            name: '',
            _hasNoTechnicalId: true,
            _originalIndex: i
          });
        }
      }
    }

    // 🔧 FIX Firefox 2026-04-07: Compléter les LOT/PAR manquants depuis le JSON de l'avis.
    // Sur Firefox, le backend indexe les lots de manière asynchrone : /list-ids peut retourner
    // 2 LOTs alors que 3 ont été créés, car le 3ème n'est pas encore dans la table duplicable_entity.
    // getAvisWithAllDuplicablesElements retourne bien le JSON complet avec les 3 lots.
    // On compare les IDs du JSON avec ceux de la liste et on ajoute les manquants.
    // 🔧 FIX 2026-04-15: Ignorer l'index 0 (template/init) qui ne doit jamais être compté comme lot réel.
    const lotsInAvis = ann[formKey]?.procurementprojectlot;
    if (Array.isArray(lotsInAvis)) {
      const idsInList = new Set(
        enrichedList
          .filter(e => e?.id && (e.id.toUpperCase().includes('LOT') || e.id.toUpperCase().includes('PAR')))
          .map(e => e.id.toUpperCase())
      );
      // Commencer à l'index 1 : l'index 0 est toujours le template/init backend
      for (let i = 1; i < lotsInAvis.length; i++) {
        const lotId = lotsInAvis[i]?.id?.val;
        if (lotId && !idsInList.has(lotId.toUpperCase())) {
          enrichedList.push({ id: lotId, name: '', _fromAvisJSON: true });
          idsInList.add(lotId.toUpperCase());
        }
      }
    }

    return enrichedList;
  }

  /**
   * Vérifie si un élément duplicable est valide (contient des données significatives).
   * Un élément est considéré valide s'il a un ID technique ou s'il contient des données
   * autres que des objets vides ou des valeurs null.
   *
   * @param element L'élément à vérifier
   * @param type Le type de l'élément (CON, TEN, RES, TPA)
   * @returns true si l'élément est valide
   */
  private isValidDuplicableElement(element: any, type: string): boolean {
    if (!element || typeof element !== 'object') return false;

    // Si l'élément a un ID avec une valeur, c'est un élément valide
    if (element.id?.val) return true;

    // Pour TPA (TenderingParty) : sans id.val propre, l'élément n'est pas un vrai TPA persisté.
    // Le fait qu'il contienne des références à des organisations via `tenderer` ne suffit pas :
    // ces références sont ajoutées quand on remplit OPT-300-Tenderer dans un sous-groupe,
    // mais elles peuvent être supprimées sans que le TPA lui-même soit réellement créé.
    // Sans ce guard, la suppression de GR-Tenderer_1 (qui avait OPT-300-Tenderer rempli)
    // provoquait la création fantôme d'un TPA-NOID dans enrichListWithElementsFromAvis.
    if (type === 'TPA') return false;

    // Vérifier si l'élément contient des données significatives (pas juste des objets vides)
    return this.hasSignificantData(element);
  }

  /**
   * Vérifie récursivement si un objet contient des données significatives
   * (valeurs non null, non undefined, et non des objets vides).
   */
  private hasSignificantData(obj: any, depth: number = 0): boolean {
    // Limiter la profondeur de récursion pour éviter les problèmes de performance
    if (depth > 10) return false;

    if (obj === null || obj === undefined) return false;

    // Une valeur primitive non vide est significative
    if (typeof obj !== 'object') {
      return obj !== '' && obj !== null && obj !== undefined;
    }

    // Pour les tableaux, vérifier si au moins un élément a des données significatives
    if (Array.isArray(obj)) {
      return obj.some(item => this.hasSignificantData(item, depth + 1));
    }

    // Pour les objets, vérifier les propriétés
    const keys = Object.keys(obj);
    if (keys.length === 0) return false;

    // Ignorer les objets qui ne contiennent que des propriétés 'val' à null
    for (const key of keys) {
      const value = obj[key];
      if (key === 'val' && value !== null && value !== undefined && value !== '') {
        return true;
      }
      if (key !== 'val' && this.hasSignificantData(value, depth + 1)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Lance un appel pour récupérer tous les elements duplicables d'un avis
   *
   * @param avisId
   */
  public getAvis(avisId: number): Observable<any> {
    return this.http.get(
      Endpoints.DUPLICABLE_ENTITY
      + Endpoints.GET_AVIS
      + Endpoints.AVIS_ID
      + avisId
    );
  }


  /**
   * Crée les entités duplicable pour un avis lié
   *
   * @param
   * @param avisId
   * @param avisIdParent
   */
  public createAvisLie(avis: any, avisId: number, avisIdParent: number, modalite: number): Observable<any> {
    return this.http.post(
      Endpoints.DUPLICABLE_ENTITY
      + Endpoints.CREATE_AVISLIE
      + Endpoints.AVIS_ID
      + avisId
      + Endpoints.AVIS_ID_PARENT
      + avisIdParent
      + Endpoints.MODALITE
      + modalite,
      avis
    );
  }

  /**
   * Récupére une entité duplicable à partir de son index et de l'id de l'avis
  /**
   * @param avisId
   * @param index
   * @param type
   */
  public getDuplicableEntity(avisId: number, index: number, type: string): Observable<any> {
    this.actuaEntityIndex = index - 1;
    if (type === 'LOT' || type === 'PAR') {
      this.actuallyIndexLotOrPar = this.actuaEntityIndex;
      // 🔧 FIX 2026-04-22: Notifier le changement d'index LOT/PAR via le callback
      if (this.onLotIndexChanged && this.actuaEntityIndex >= 0) {
        this.onLotIndexChanged(type, this.actuaEntityIndex);
      }
    }
    this.canNavigateInStepperService.eformAllInputCreated = false;
    this.indexTrackerService.setLastIndex(type, index);

    return this.http.get(Endpoints.DUPLICABLE_ENTITY
      + Endpoints.AVIS_ID + avisId
      + Endpoints.INDEX + index
      + Endpoints.TYPE + type);
  }

  /**
   * 🔧 FIX 2026-04-22: Callback pour notifier le changement d'index LOT/PAR.
   * Injecté depuis le stepper pour appeler setDisplayedRealIndex sans dépendance circulaire.
   */
  public onLotIndexChanged: (type: string, index: number) => void = null;


  /**
   * Récupère la liste des index correspondants aux lots ou au par pour leur placement dans le stepper
   * ✅ FIX 2026-03-18: Utiliser stepperLotPageCount (nombre réel de pages LOT dans le stepper)
   * au lieu de compter dynamiquement depuis listDuplicableEntity, car la liste peut contenir
   * des éléments LOT/PAR supplémentaires après duplication/import.
   */
  public getSectionDuplicableIndexes() {
    const count = this.stepperLotPageCount > 0
      ? this.stepperLotPageCount
      : this.listDuplicableEntity.getValue().filter(e => e.id.includes('LOT') || e.id.includes('PAR')).length;
    return Array.from({length: count}, (_, i) => this.initialSectionDuplicableIndex + i);
  }

  /**
   * Vérifie si la step sur laquelle on est, est une section duplicable
   */
  public isSectionDuplicableStep(stepIndex: number): boolean {
    const indexes = this.getSectionDuplicableIndexes();
    return indexes.includes(stepIndex);
  }

  /**
   * Duplique le lot initial pour en créer un nouveau
   *
   * @param avisId
   * @param formulaireName
   */
  public addNewDuplicableEntity(avisId: number, type: string): Observable<any> {
    return this.http.get(Endpoints.DUPLICABLE_ENTITY
      + Endpoints.ADD_DUPLICABLE_ENTITY
      + Endpoints.AVIS_ID + avisId
      + Endpoints.TYPE + type);
  }

  public extractDuplicableElementIfNeeded(notice) {
    //On récupère les elements duplicable si elles existent
    const conditionOrg = (id: string) => id === 'GR-Organisations';
    this.organisationNotice = JSON.parse(JSON.stringify(this.findObjectById(notice, conditionOrg)));
    //Suppression temporaire du point de contact spécifique
    if(this.organisationNotice?.subGroups){
      this.organisationNotice.subGroups = this.organisationNotice.subGroups.filter(g=>g.id !='GR-Touch-Point');
    }

    const conditionRes = (id: string) => id === 'GR-LotResult';
    this.resultNotice = JSON.parse(JSON.stringify(this.findObjectById(notice, conditionRes)));
    //Suppression temporaire du point de contact spécifique
    if(this.resultNotice?.subGroups){
      this.resultNotice.subGroups = this.resultNotice.subGroups.filter(g=>g.id !='GR-Touch-Point');
    }

    const conditionTen = (id: string) => id === 'GR-LotTender';
    this.tenderNotice = JSON.parse(JSON.stringify(this.findObjectById(notice, conditionTen)));
    //Suppression temporaire du point de contact spécifique
    if(this.tenderNotice?.subGroups){
      this.tenderNotice.subGroups = this.tenderNotice.subGroups.filter(g=>g.id !='GR-Touch-Point');
    }

    const conditionCon = (id: string) => id === 'GR-SettledContract';
    this.settledContractNotice = JSON.parse(JSON.stringify(this.findObjectById(notice, conditionCon)));
    //Suppression temporaire du point de contact spécifique
    if(this.settledContractNotice?.subGroups){
      this.settledContractNotice.subGroups = this.settledContractNotice.subGroups.filter(g=>g.id !='GR-Touch-Point');
    }

    const conditionTpa = (id: string) => id === 'GR-TenderingParty';
    this.tenderingPartyNotice = JSON.parse(JSON.stringify(this.findObjectById(notice, conditionTpa)));
    //Suppression temporaire du point de contact spécifique
    if(this.tenderingPartyNotice?.subGroups){
      this.tenderingPartyNotice.subGroups = this.tenderingPartyNotice.subGroups.filter(g=>g.id !='GR-Touch-Point');
    }

  }

  //On parcours une notice est on remonte un élément quand on trouve un objet qui remplie une condition
  private findObjectById(notice: any | any[], condition: (id: string) => boolean): any {
    // Si l'entrée est un tableau, on parcourt chaque élément
    if (Array.isArray(notice)) {
      for (const item of notice) {
        const result = this.findObjectById(item, condition);
        if (result) {
          return result; // Retourne dès qu'un match est trouvé
        }
      }
      return null; // Aucun match trouvé dans le tableau
    }

    // Si l'entrée est un objet
    if (notice && typeof notice === 'object') {
      // Vérifie si l'objet actuel a un id qui remplit la condition
      if (typeof notice.id === 'string' && condition(notice.id)) {
        return notice;
      }

      // Parcourt les propriétés de l'objet
      for (const key in notice) {
        if (notice.hasOwnProperty(key)) {
          const value = notice[key];

          // Appelle la fonction récursivement si la valeur est un objet ou un tableau
          const result = this.findObjectById(value, condition);
          if (result) {
            return result; // Retourne dès qu'un match est trouvé
          }
        }
      }
    }

    // Aucun match trouvé
    return null;
  }

  public getAvisWithAllDuplicablesElements(avisId: number): Observable<void> {
    return this.getAvis(avisId).pipe(
      tap((data) => {
        const lowered = this.eformsToolsService.propertyNamesToLowercase(data);
        this.avisWithElementsDuplicable.next(lowered);
      })
    );
  }


  public constructPathWithIndexForDuplicableElement(pathOld: string, pathToModifie: string, indexWanted : number, hasNoConditionsCreationMap: boolean = false, isForDelete: boolean = false): string{
    let pathToReturn = '';
    let paths = pathToModifie.replace('undefined.', '').replace('undefined', '').split('.').filter(e => !e.includes('val'));

    // Stocker une copie avant le reverse pour les vérifications
    const pathsCopy = [...paths];
    paths.reverse();
    const lastArrayElement = paths.find(item => item.includes('[]')); // Le dernier élément avec []

    // Identifier le tableau duplicable principal (organization, procurementprojectlot, etc.)
    // C'est à ce tableau qu'on doit appliquer indexWanted, pas au dernier tableau imbriqué
    // Utiliser pathsCopy (non inversé) pour la recherche
    const duplicablePrincipalArrays = ['organization[]', 'procurementprojectlot[]', 'settledcontract[]',
                                        'tenderingparty[]', 'tender[]', 'lotresult[]'];
    const principalArray = pathsCopy.find(item => duplicablePrincipalArrays.includes(item.toLowerCase()));

    // Tableaux imbriqués dans organization qui ne doivent PAS recevoir indexWanted
    const nestedArraysInOrg = ['partylegalentity[]', 'ultimatebeneficialowner[]', 'partyname[]'];
    const isLastArrayNested = lastArrayElement && nestedArraysInOrg.includes(lastArrayElement.toLowerCase());


    // boolean permettant de savoir si le pathOld et le pathToModifie sont les mêmes.
    // Pour les comparer on enlève .val à l'un, et les chiffres à l'autre.
    let isSamePath = false;
    let tempPathToModifie = pathToModifie;
    if (tempPathToModifie && tempPathToModifie.endsWith('.val')) {
      tempPathToModifie = tempPathToModifie.slice(0, -4);
    }
    let tempPathOld = pathOld ? pathOld.replace(/\[\]\d+/g, '[]') : pathOld;
    if (tempPathOld === tempPathToModifie) {
      isSamePath = true;
    }

    paths.reverse();
    for (const iterator of paths) {
      if (pathToReturn != '') {
        if (iterator.endsWith('[]')) {
          let ind = this.getNumberAfterElement(pathOld, iterator) ?? 0
          if (iterator === 'ublextension[]') {
            pathToReturn = pathToReturn + '.' + iterator + '0';
          } else if (iterator === lastArrayElement) {
            // ✅ FIX: Si le dernier tableau est un tableau imbriqué (partylegalentity, etc.),
            // utiliser l'index de pathOld ou 0, PAS indexWanted
            // indexWanted doit être appliqué au tableau principal (organization, etc.)
            if (isLastArrayNested && principalArray) {
              // Utiliser l'index existant dans pathOld, ou 0 si non trouvé
              pathToReturn = pathToReturn + '.' + iterator + ind;
            } else {
              // On utilise indexWanted car c'est le tableau principal
              pathToReturn = pathToReturn +'.'+ iterator + indexWanted;
            }
          } else if (principalArray && iterator.toLowerCase() === principalArray.toLowerCase()) {
            // ✅ FIX: Appliquer indexWanted au tableau principal
            pathToReturn = pathToReturn + '.' + iterator + indexWanted;
          } else {
            pathToReturn = pathToReturn + '.' + iterator + ind;
          }
        } else {
          pathToReturn = pathToReturn + '.' + iterator;
        }
      } else {
        pathToReturn = iterator;
      }
    }

    return pathToReturn;
  }

  private getNumberAfterElement(path: string, element: string): number | null {
    // Créer une expression régulière qui recherche l'élément suivi de chiffres
    const regex = new RegExp(`${element.replace('[', '\\[')}(\\d+)`);

    // Appliquer la regex à la chaîne path
    const match = path?.match(regex);

    // Si un match est trouvé, retourner le nombre extrait
    if (match) {
      return parseInt(match[1], 10);  // match[1] contiendra le nombre qui suit 'b[]'
    }

    // Si aucune correspondance n'est trouvée, retourner null
    return null;
  }

  private extraireNombresAssocies(chaine) {
    const nombresAssocies = {};
    const regex = /([\w]+)\[\](\d+)/g;
    let match;

    while ((match = regex.exec(chaine)) !== null) {
      nombresAssocies[match[1]] = match[2]; // Associe le préfixe (clé) avec son nombre
    }

    return nombresAssocies;
  }

  private remplacerNombresDansChaine(chaine, nombresAssocies) {
    return chaine.replace(/([\w]+)\[\]\d*/g, (match, key) => {
      if (nombresAssocies[key] !== undefined) {
        return `${key}[]${nombresAssocies[key]}`;
      }
      return match;
    });
  }

  private modifierChaine(chaine) {
    const elementParent = chaine.split(".")[0];
    const parties = chaine.split(elementParent).filter(e => e != '');

    if (parties.length != 2) return chaine; // Vérifier qu'on a bien deux parties

    const nombresAssocies = this.extraireNombresAssocies(parties[0]); // Associe les clés aux nombres

    // Modifier la deuxième partie
    const nouvellePartie = this.remplacerNombresDansChaine(parties[1], nombresAssocies);

    return elementParent + nouvellePartie; // Réassembler la chaîne finale
  }

  setValueFromPath(obj: any, path: string, keyToModify: string, newValue: any): boolean {
    const keys = path.split('.'); // Séparer les clés du chemin
    let current = obj;

    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      const match = key.match(/(.*?)\[\](\d+)/); // Vérifie si c'est un tableau (ex: "lotresult[]0")

      if (match) {
        const arrayKey = match[1]; // Ex: "lotresult"
        const index = parseInt(match[2], 10); // Ex: 0

        if (!current || typeof current !== 'object') {
          current = {};
        }

        if (!Array.isArray(current[arrayKey])) {
          current[arrayKey] = []; // Crée un tableau vide si inexistant
        }

        if (current[arrayKey].length <= index) {
          // Si l'index n'existe pas encore, on le crée
          while (current[arrayKey].length <= index) {
            current[arrayKey].push({}); // Ajoute un objet vide à l'index
          }
        }

        current = current[arrayKey][index]; // Accède à l'élément du tableau
      } else {

        if (!current || typeof current !== 'object') {
          current = {};
        }

        if (!(key in current) || current[key] == null) {
          current[key] = {}; // Crée un objet vide si la clé n'existe pas
        }

        current = current[key]; // Accède à la clé normale
      }
    }

    current[keyToModify] = newValue;
    return true;
  }

  initializeIndexTracker(): void {
    this.listDuplicableEntity.subscribe(entities => {
      if (entities && entities.length > 0) {
        this.indexTrackerService.updateCountsFromList(entities);
      }
    });
  }
}
